import { FC, useMemo } from 'react'
import * as styles from 'components/features/pfm/Dashboard2020Page/Dashboard2020Page.styl'
import { InternalPageRedirect } from 'components/features/pfm/InternalPageRedirect/InternalPageRedirect'
import InternalLayoutContainer from 'components/features/pfm/InternalLayout/InternalLayoutContainer'
import InternalSidebarContainer from 'components/features/pfm/InternalSidebar/InternalSidebarContainer'
import { TwoColumnRowLayout, SingleColumnLayout } from 'components/layouts/Layouts'
import { NotificationTrayWrapper } from 'components/features/pfm/NotificationTray/NotificationTrayWrapper'
import { QuickLookSection } from 'components/features/pfm/QuickLookSection/QuickLookSection'
import { NETWORTH_PAGE } from 'constants/pages'
import ModuleWrapper2020 from 'components/features/pfm/ModuleWrapper2020/ModuleWrapper2020'
import { GenericTrendModuleContainer } from 'components/features/pfm/LineChart/GenericTrendModuleContainer'
import { RecentTransactionsModule } from 'components/features/pfm/RecentTransactionsModule/RecentTransactionsModule'
import { MoneyflowDailyTrendContainer } from 'components/features/pfm/LineChart/MoneyflowDailyTrendContainer'
import { ALL_CATEGORY_ID } from 'constants/meta'
import { DEFAULT_TIMEFRAMES } from 'constants/datetime'
import { NavigationLink } from 'components/atoms/navigation/NavigationLink'
import CreditSummaryModuleContainer from 'components/features/pfm/CreditSummaryModule/CreditSummaryModuleContainer'
import { GenericRankChartContainer } from 'components/features/pfm/LineChart/GenericRankChartContainer'
import { FeedPopularTags } from 'components/features/socialFeed/FeedPopularTags/FeedPopularTags'
import { TopSpendingCategoriesModule } from 'components/features/pfm/TopSpendingCategoriesModule/TopSpendingCategoriesModule'
import { AccountsSummaryModule } from 'components/features/pfm/AccountsSummaryModule/AccountsSummaryModule'
import { DASHBOARD_CREDIT_HEADER_HINT, DASHBOARD_NETWORTH_HEADER_HINT } from 'constants/hints'
import SvgIcon from 'components/atoms/SvgIcon'
import ChevronSvg from 'icons/chevron.svg'
import MoneyflowTrackersModuleContainer from 'components/features/pfm/MoneyflowTrackersModule/MoneyflowTrackersModuleContainer'
import { PageAnchorHandler } from 'handlers/PageAnchorHandler/PageAnchorHandler'
import { RewardsSummaryModule } from 'components/features/rewards/rewardPageSummary/RewardsSummaryModule/RewardsSummaryModule'
import { ToggleComparison } from 'components/features/pageFilters/ToggleComparison/ToggleComparison'
import cn from 'classnames'
import { withComparisonSelector } from 'components/features/pageFilters/selectors'
import { useStoreSelector } from 'helpers/reactReduxHelpers'
import { useSimpleGuard } from 'helpers/loadableHelpers'
import { memberInfoGuard } from 'guards/globalGuards'

const DEFAULT_TIMEFRAME = DEFAULT_TIMEFRAMES.dashboard

type Dashboard2020PageProps = {

}

export const Dashboard2020Page: FC<Dashboard2020PageProps> = (props) => {
  const {} = props
  useSimpleGuard(memberInfoGuard, props, 'Dashboard2020Page')

  const withComparison = useStoreSelector(withComparisonSelector)

  const notifications = useMemo(() => (
    <NotificationTrayWrapper
      renderLevel='page'
      placement='dashboard_general'
      isPositionRelative
    />
  ), [])

  const recentTransactionsChild = useMemo(() => (
    <div className={ styles.module }>
      <ModuleWrapper2020
        title='Recent Transactions'
        noPadding
        shadowTheme='medium'
        forceRoundedCorners
        action={
          <NavigationLink className={ styles.headerLink } to='transactions' title='See ALL your transactions'>
            See All
            <div className={ styles.chevron }><SvgIcon src={ ChevronSvg } /></div>
          </NavigationLink>
        }
      >
        <RecentTransactionsModule />
      </ModuleWrapper2020>
    </div>
  ), [])

  const spendingChild = useMemo(() => (
    <div className={ styles.module } key='1'>
      <ModuleWrapper2020
        title='Spending'
        anchor='dashboard_spending'
        noPadding
        shadowTheme='medium'
        forceRoundedCorners
        action={
          <NavigationLink className={ styles.headerLink } to='spending' title='Learn more about your spending'>
            Details
            <div className={ styles.chevron }><SvgIcon src={ ChevronSvg } /></div>
          </NavigationLink>
        }
      >
        <div className={ styles.chart }>
          <MoneyflowDailyTrendContainer
            type='spending'
            timeframe={ DEFAULT_TIMEFRAME }
            categoryId={ ALL_CATEGORY_ID }
            fullHeight
          />
        </div>
      </ModuleWrapper2020>
    </div>
  ), [])

  const topSpendingCategories = useMemo(() => (
    <div className={ styles.module } key='2'>
      <ModuleWrapper2020
        title='Top Spending Categories'
        noPadding
        card={ false }
        action={
          <NavigationLink className={ styles.headerLink } to='spending' title='See ALL your spending categories'>
            See All
            <div className={ styles.chevron }><SvgIcon src={ ChevronSvg } /></div>
          </NavigationLink>
        }
      >
        <TopSpendingCategoriesModule
          type='spending'
          timeframe={ DEFAULT_TIMEFRAME }
        />
      </ModuleWrapper2020>
    </div>
  ), [])

  const rewardsSummaryChild = useMemo(() => (
    <div className={ styles.module }>
      <RewardsSummaryModule
        mode='dashboard'
        action={
          <NavigationLink className={ styles.headerLink } to='rewards' title='Learn more about your rewards'>
            Details
            <div className={ styles.chevron }><SvgIcon src={ ChevronSvg } /></div>
          </NavigationLink>
        }
      />
    </div>
  ), [])

  const netWorthChild = useMemo(() => (
    <div className={ styles.module }>
      <ModuleWrapper2020
        title='Net Worth'
        anchor='dashboard_networth'
        noPadding
        shadowTheme='medium'
        forceRoundedCorners
        hint={ DASHBOARD_NETWORTH_HEADER_HINT }
        action={
          <NavigationLink className={ styles.headerLink } to='linkedaccounts' title='See all your linked accounts'>
            Your Accounts
            <div className={ styles.chevron }><SvgIcon src={ ChevronSvg } /></div>
          </NavigationLink>
        }
      >
        <div className={ styles.chart }>
          <GenericTrendModuleContainer type={ NETWORTH_PAGE } timeframe='6-months' fullHeight />
        </div>
      </ModuleWrapper2020>
    </div>
  ), [])

  const netWorthRankingChild = useMemo(() => (
    <div className={ cn(styles.module, !withComparison && styles.hide) }>
      <ModuleWrapper2020
        title='Net Worth Ranking'
        anchor='dashboard_networth_ranking'
        noPadding
        shadowTheme='medium'
        forceRoundedCorners
      >
        <div className={ styles.chart }>
          <GenericRankChartContainer
            type={ NETWORTH_PAGE }
            timeframe={ DEFAULT_TIMEFRAME }
            fullHeight
          />
        </div>
      </ModuleWrapper2020>
    </div>
  ), [withComparison])

  const creditScoreChild = useMemo(() => (
    <div className={ styles.module }>
      <ModuleWrapper2020
        title='Credit Score'
        anchor='dashboard_creditscore'
        noPadding
        shadowTheme='medium'
        forceRoundedCorners
        hint={ DASHBOARD_CREDIT_HEADER_HINT }
        action={
          <NavigationLink className={ styles.headerLink } to='creditscore' title='Learn more about your credit profile'>
            Details
            <div className={ styles.chevron }><SvgIcon src={ ChevronSvg } /></div>
          </NavigationLink>
        }
      >
        <div className={ styles.creditChart }>
          <CreditSummaryModuleContainer timeframe={ DEFAULT_TIMEFRAME } />
        </div>
      </ModuleWrapper2020>
    </div>
  ), [])

  const feedPopularTagsChild = useMemo(() => (
    <div className={ styles.module }>
      <ModuleWrapper2020
        shadowTheme='medium'
        noPadding
        forceRoundedCorners
      >
        <div className={ styles.feedPopularTags }>
          <div className={ styles.feedPopularTagsInner }>
            <FeedPopularTags />
          </div>
        </div>
      </ModuleWrapper2020>
    </div>
  ), [])

  const assetsChild = useMemo(() => (
    <div className={ styles.module }>
      <ModuleWrapper2020
        title='Assets'
        anchor='dashboard_assets'
        shadowTheme='medium'
        noPadding
        forceRoundedCorners
        action={
          <NavigationLink className={ styles.headerLink } to='assets' title='Learn more about your assets'>
            Details
            <div className={ styles.chevron }><SvgIcon src={ ChevronSvg } /></div>
          </NavigationLink>
        }
      >
        <div className={ styles.content }>
          <AccountsSummaryModule timeframe={ DEFAULT_TIMEFRAME } type='assets' />
        </div>
      </ModuleWrapper2020>
    </div>
  ), [])

  const debtsChild = useMemo(() => (
    <div className={ styles.module }>
      <ModuleWrapper2020
        title='Debts'
        anchor='dashboard_debts'
        shadowTheme='medium'
        noPadding
        forceRoundedCorners
        action={
          <NavigationLink className={ styles.headerLink } to='debts' title='Learn more about your debts'>
            Details
            <div className={ styles.chevron }><SvgIcon src={ ChevronSvg } /></div>
          </NavigationLink>
        }
      >
        <div className={ styles.content }>
          <AccountsSummaryModule timeframe={ DEFAULT_TIMEFRAME } type='debts' />
        </div>
      </ModuleWrapper2020>
    </div>
  ), [])


  return (
    <>
      <PageAnchorHandler placement='dashboard'>
        <div className={ styles.anchorPlaceholder } />
      </PageAnchorHandler>
      <PageAnchorHandler placement='dashboard_general'>
        <div className={ styles.anchorPlaceholder } />
      </PageAnchorHandler>
      <InternalPageRedirect>
        <InternalLayoutContainer
          sidebar={ <InternalSidebarContainer /> }
          notifications={ notifications }
        >
          <QuickLookSection />
          <SingleColumnLayout>
            <ToggleComparison className={ styles.comparison } />
            <div className={ styles.trackerModule }>
              <MoneyflowTrackersModuleContainer title='Spending Trackers' type='spending' timeframe={ DEFAULT_TIMEFRAME } />
            </div>
            <div className={ styles.trackerModule }>
              <MoneyflowTrackersModuleContainer title='Income Trackers' type='income' timeframe={ DEFAULT_TIMEFRAME } />
            </div>
            <TwoColumnRowLayout left={ [spendingChild, topSpendingCategories] } right={ recentTransactionsChild } />
            <TwoColumnRowLayout left={ netWorthChild } right={ netWorthRankingChild } />
            <TwoColumnRowLayout left={ assetsChild } right={ debtsChild } />
            <TwoColumnRowLayout left={ creditScoreChild } right={ rewardsSummaryChild } />
            { feedPopularTagsChild }
          </SingleColumnLayout>
        </InternalLayoutContainer>
      </InternalPageRedirect>
    </>
  )
}
